// src/pages/StudyEditPage.js - 연구 입력 폼 수정 페이지

import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import UpgradeModal from "../components/UpgradeModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Icon24, Toast } from "../components/ui";
import WorkspaceLayout from "../components/WorkspaceLayout";
import StudyEditChatPanel from "../features/study/components/StudyEditChatPanel";
import {
  methodologyOptions,
  useStudyEditPage,
} from "../features/study/hooks/useStudyEditPage";
import "./NewStudyPage.scss"; // 기본 스타일 재사용
import "./StudyEditPage.scss"; // 편집 페이지 전용 스타일

const StudyEditPage = () => {
  const { projectSlug, studySlug } = useParams(); // slug로 변경
  const navigate = useNavigate();
  const {
    chatEndRef,
    chatInputRef,
    chatInput,
    chatMessages,
    copiedIndex,
    customMethodology,
    editingFields,
    formData,
    hasChanges,
    isChatLoading,
    isChatMinimized,
    isCustomMethodologyComposing,
    loading,
    project,
    showCustomInput,
    study,
    toast,
    upgradeModal,
    finishEdit,
    handleAddCustomMethodology,
    handleGoBack,
    handleInputChange,
    handleMethodologyToggle,
    handleRegeneratePlan,
    handleTextareaChange,
    isExpanded,
    sendChatMessage,
    setChatInput,
    setCopiedIndex,
    setCustomMethodology,
    setEditingFields,
    setFormData,
    setIsChatMinimized,
    setIsCustomMethodologyComposing,
    setShowCustomInput,
    setToast,
    setUpgradeModal,
    toggleEditMode,
    toggleExpand,
  } = useStudyEditPage({
    projectSlug,
    studySlug,
    navigate,
  });


  if (loading) {
    return (
      <WorkspaceLayout hideCollapseButton={false}>
        <div className="new-study-container">
          <div className="loading-container">
            <FontAwesomeIcon icon={faSpinner} className="loading-spinner" spin />
            <p>연구 데이터를 불러오는 중...</p>
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  if (!project) {
    return (
      <WorkspaceLayout hideCollapseButton={false}>
        <div className="new-study-container">
          <div className="error-container">
            <h2>프로젝트를 찾을 수 없습니다</h2>
            <button onClick={handleGoBack} className="btn btn-primary">
              프로젝트 목록으로 돌아가기
            </button>
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout hideCollapseButton={false}>
      <div className="new-study-container">
        {/* 헤더 */}
        <div className="new-study-header">
          <button className="back-button" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="header-info">
            <h1>입력한 정보</h1>
            <p className="header-subtitle">{formData.studyName || "로딩 중..."}</p>
          </div>
        </div>

        {/* 메인 콘텐츠 */}
        <div className="new-study-content">
          {/* 좌측: 카드 영역 */}
          <div className="form-section">
            <div className="cards-container">
              {/* 재생성 버튼 - 오른쪽 위 */}
              <div className="study-edit-regenerate-section">
              <button
                className="btn btn-primary"
                onClick={handleRegeneratePlan}
              >
                <Icon24 name="rotate" />
                {hasChanges ? "수정한 정보로 재생성" : "계획서 재생성"}
              </button>
              </div>


              {/* 문제 정의 */}
              <div className="study-edit-field">
                <div className="field-label">
                  <span>문제 정의</span>
                  {!editingFields.problemDefinition && (
                    <button
                      className="study-edit-edit-btn"
                      onClick={() => toggleEditMode("problemDefinition")}
                    >
                      <Icon24 name="rename" />
                    </button>
                  )}
                </div>
                <div className="field-content">
                  {editingFields.problemDefinition ? (
                    <div className="edit-mode">
                      <textarea
                        className="card-textarea"
                        placeholder="예: 현재 모바일 앱의 사용자 이탈률이 높은 이유를 파악하고 개선 방안을 찾고자 합니다."
                        value={formData.problemDefinition}
                        onChange={(e) => handleTextareaChange("problemDefinition", e.target.value, e)}
                        autoFocus
                        rows={1}
                      />
                      <div className="study-edit-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => finishEdit("problemDefinition")}
                        >
                          저장
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditingFields((prev) => ({
                              ...prev,
                              problemDefinition: false,
                            }));
                            if (study) {
                              setFormData((prev) => ({
                                ...prev,
                                problemDefinition: study.initial_input || "",
                              }));
                            }
                          }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="display-mode">
                      <span className="display-text">
                        {formData.problemDefinition || "문제 정의를 입력해주세요"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 어떤 방법으로 조사 */}
              <div className="study-edit-field">
                <div className="field-label">
                  <span>리서치 방법론</span>
                  {!editingFields.methodologies && (
                    <button
                      className="study-edit-edit-btn"
                      onClick={() => toggleEditMode("methodologies")}
                    >
                      <Icon24 name="rename" />
                    </button>
                  )}
                </div>
                <div className="field-content">
                  {editingFields.methodologies ? (
                    <div className="edit-mode">
                      <div className="methodology-tags">
                        {methodologyOptions.map((method) => (
                          <button
                            key={method}
                            className={`method-tag ${
                              formData.methodologies.includes(method) ? "active" : ""
                            }`}
                            onClick={() => handleMethodologyToggle(method)}
                          >
                            {method}
                          </button>
                        ))}
                        {formData.methodologies
                          .filter((method) => !methodologyOptions.includes(method))
                          .map((method) => (
                            <button
                              key={method}
                              className={`method-tag ${
                                formData.methodologies.includes(method) ? "active" : ""
                              }`}
                              onClick={() => handleMethodologyToggle(method)}
                            >
                              {method}
                            </button>
                          ))}
                        {!showCustomInput ? (
                          <button
                            className="method-tag custom-input-tag"
                            onClick={() => setShowCustomInput(true)}
                          >
                            직접 입력
                          </button>
                        ) : (
                          <div className="custom-input-container">
                            <input
                              type="text"
                              className="method-tag-input"
                              placeholder="수행하고자 하는 방법을 입력 후 Enter"
                              value={customMethodology}
                              onChange={(e) => {
                                setCustomMethodology(e.target.value);
                              }}
                              onCompositionStart={() => setIsCustomMethodologyComposing(true)}
                              onCompositionEnd={(e) => {
                                setIsCustomMethodologyComposing(false);
                                setCustomMethodology(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !isCustomMethodologyComposing) {
                                  e.preventDefault();
                                  const trimmed = customMethodology.trim();
                                  if (trimmed) {
                                    handleAddCustomMethodology(trimmed);
                                    setCustomMethodology("");
                                  }
                                } else if (e.key === "Escape" && !isCustomMethodologyComposing) {
                                  setCustomMethodology("");
                                  setShowCustomInput(false);
                                }
                              }}
                              onBlur={() => {
                                const trimmed = customMethodology.trim();
                                if (trimmed) {
                                  handleAddCustomMethodology(trimmed);
                                }
                                setCustomMethodology("");
                                setShowCustomInput(false);
                              }}
                              autoFocus
                            />
                          </div>
                        )}
                      </div>
                      <div className="study-edit-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => finishEdit("methodologies")}
                        >
                          저장
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditingFields((prev) => ({
                              ...prev,
                              methodologies: false,
                            }));
                            setShowCustomInput(false);
                            setCustomMethodology("");
                            if (study) {
                              setFormData((prev) => ({
                                ...prev,
                                methodologies: study.methodologies || [],
                              }));
                            }
                          }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="display-mode">
                      <span className="display-text">
                        {formData.methodologies.length > 0 
                          ? formData.methodologies.join(", ") 
                          : "조사 방법을 선택해주세요"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 조사 대상 */}
              <div className="study-edit-field">
                <div className="field-label">
                  <span>대상 및 모집기준</span>
                  {!editingFields.targetAudience && (
                    <button
                      className="study-edit-edit-btn"
                      onClick={() => toggleEditMode("targetAudience")}
                    >
                      <Icon24 name="rename" />
                    </button>
                  )}
                </div>
                <div className="field-content">
                  {editingFields.targetAudience ? (
                    <div className="edit-mode">
                      <div className="target-audience-row">
                        <div className="participant-count-field">
                          <label className="field-sublabel">규모</label>
                          <div className="input-with-unit">
                            <input
                              type="number"
                              className="card-input"
                              value={formData.participantCount}
                              onChange={(e) => handleInputChange("participantCount", e.target.value)}
                            />
                            <span className="input-unit">명</span>
                          </div>
                        </div>
                        <div className="target-audience-field">
                          <label className="field-sublabel">구분</label>
                          <textarea
                            className="card-input auto-textarea"
                            placeholder="예: 20-30대 직장인, 스마트폰 사용자 등"
                            value={formData.targetAudience}
                            onChange={(e) => handleTextareaChange("targetAudience", e.target.value, e)}
                            rows={1}
                          />
                        </div>
                      </div>
                      <div className="study-edit-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => finishEdit("targetAudience")}
                        >
                          저장
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditingFields((prev) => ({
                              ...prev,
                              targetAudience: false,
                            }));
                            if (study) {
                              setFormData((prev) => ({
                                ...prev,
                                targetAudience: study.target_audience || "",
                                participantCount: study.participant_count != null ? String(study.participant_count) : "",
                              }));
                            }
                          }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="display-mode">
                      <div className="target-audience-display">
                        <span className="display-text">
                          규모: {formData.participantCount ? `${formData.participantCount}명` : "-"}
                        </span>
                        <span className="display-text">
                          구분: {formData.targetAudience || "미정"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

              </div>
                
              {/* 희망 일정 */}
              <div className="study-edit-field">
                <div className="field-label">
                  <span>희망 일정</span>
                  {!editingFields.timeline && (
                    <button
                      className="study-edit-edit-btn"
                      onClick={() => toggleEditMode("timeline")}
                    >
                      <Icon24 name="rename" />
                    </button>
                  )}
                </div>
                <div className="field-content">
                  {editingFields.timeline ? (
                    <div className="edit-mode">
                      <div className="timeline-row">
                        <div className="date-field">
                          <label className="field-sublabel">희망하는 시작 일정</label>
                          <input
                            type="date"
                            className="card-input"
                            value={formData.startDate}
                            onChange={(e) => handleInputChange("startDate", e.target.value)}
                          />
                        </div>
                        <div className="date-field">
                          <label className="field-sublabel">예상 종료 일정</label>
                          <input
                            type="date"
                            className="card-input"
                            value={formData.endDate}
                            onChange={(e) => handleInputChange("endDate", e.target.value)}
                          />
                        </div>
                        <div className="timeline-field">
                          <label className="field-sublabel">조사의 기간</label>
                          <textarea
                            className="card-input auto-textarea"
                            placeholder="예: 2주, 1달 등"
                            value={formData.timeline}
                            onChange={(e) => handleTextareaChange("timeline", e.target.value, e)}
                            rows={1}
                          />
                        </div>
                      </div>
                      <div className="study-edit-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => finishEdit("timeline")}
                        >
                          저장
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditingFields((prev) => ({
                              ...prev,
                              timeline: false,
                            }));
                            if (study) {
                              setFormData((prev) => ({
                                ...prev,
                                timeline: study.timeline || "",
                                startDate: study.start_date || "",
                                endDate: study.end_date || "",
                              }));
                            }
                          }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="display-mode">
                      <div className="timeline-display">
                        <span className="display-text">
                          시작: {formData.startDate || "미정"}
                        </span>
                        <span className="display-text">
                          종료: {formData.endDate || "미정"}
                        </span>
                        <span className="display-text">
                          기간: {formData.timeline || "미정"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 예상 예산 - 주석 처리 (계획서에 포함하지 않음) */}
              {/* <div className="study-edit-field">
                <div className="field-label">
                  <span>예상 예산</span>
                  {!editingFields.budget && (
                    <button
                      className="study-edit-edit-btn"
                      onClick={() => toggleEditMode("budget")}
                    >
                      <Icon24 name="rename" />
                    </button>
                  )}
                </div>
                <div className="field-content">
                  {editingFields.budget ? (
                    <div className="edit-mode">
                      <input
                        type="text"
                        className="edit-input"
                        placeholder="예: 500만원, 1000만원 등"
                        value={formData.budget}
                        onChange={(e) => handleInputChange("budget", e.target.value)}
                        autoFocus
                      />
                      <div className="study-edit-actions">
                        <button
                          className="study-edit-save-btn"
                          onClick={() => finishEdit("budget")}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button
                          className="study-edit-cancel-btn"
                          onClick={() => finishEdit("budget")}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="display-mode">
                      <span className="display-text">
                        {formData.budget || "예상 예산을 입력해주세요"}
                      </span>
                    </div>
                  )}
                </div>
              </div> */}

              {/* 기타 요구사항 */}
              <div className="study-edit-field">
                <div className="field-label">
                  <span>기타 요구사항</span>
                  {!editingFields.additionalRequirements && (
                    <button
                      className="study-edit-edit-btn"
                      onClick={() => toggleEditMode("additionalRequirements")}
                    >
                      <Icon24 name="rename" />
                    </button>
                  )}
                </div>
                <div className="field-content">
                  {editingFields.additionalRequirements ? (
                    <div className="edit-mode">
                      <textarea
                        className="card-input auto-textarea"
                        placeholder="예: 특정 도구 사용, 특별한 분석 방법, 제약사항 등"
                        value={formData.additionalRequirements}
                        onChange={(e) => handleTextareaChange("additionalRequirements", e.target.value, e)}
                        autoFocus
                        rows={1}
                      />
                      <div className="study-edit-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => finishEdit("additionalRequirements")}
                        >
                          저장
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditingFields((prev) => ({
                              ...prev,
                              additionalRequirements: false,
                            }));
                            if (study) {
                              setFormData((prev) => ({
                                ...prev,
                                additionalRequirements: study.additional_requirements || "",
                              }));
                            }
                          }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="display-mode">
                      <span className="display-text">
                        {formData.additionalRequirements || "기타 요구사항을 입력해주세요"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          <StudyEditChatPanel
            chatEndRef={chatEndRef}
            chatInput={chatInput}
            chatInputRef={chatInputRef}
            chatMessages={chatMessages}
            copiedIndex={copiedIndex}
            isChatLoading={isChatLoading}
            isChatMinimized={isChatMinimized}
            isExpanded={isExpanded}
            sendChatMessage={sendChatMessage}
            setChatInput={setChatInput}
            setCopiedIndex={setCopiedIndex}
            setIsChatMinimized={setIsChatMinimized}
            setToast={setToast}
            toggleExpand={toggleExpand}
          />
        </div>
      </div>
      
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ ...upgradeModal, isOpen: false })}
        reason={upgradeModal.reason}
      />
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ ...toast, isOpen: false })}
        autoClose={true}
        autoCloseDelay={2000}
      />
    </WorkspaceLayout>
  );
};

export default StudyEditPage;
